<template>
  <nav class="fixed top-0 left-0 w-full text-primary-font z-1001">
    <!-- ========== MOBILE HEADER ========== -->
    <div class="flex items-center justify-between px-2 h-15.5 lg:hidden bg-primary">
      <!-- LEFT: Logo + Text -->
      <a class="flex flex-col items-center justify-center">
        <img :src="agent?.image?.agent?.logo || logo" alt="logo" class="h-8 w-auto object-contain" />
        <span class="text-center mt-0.5 text-[0.7rem] font-semibold text-white leading-tight">
          {{ safeT('auth.poweredByAskmebet') }}
        </span>
      </a>

      <!-- RIGHT: Balance + Burger -->
      <div class="flex items-center gap-4">
        <!-- Balance -->
        <span class="flex items-center text-sm font-bold text-white">
          <i class="fas fa-money-bill mr-1 text-lg" />
          {{ formatCurrency(displayWalletBalance) }}
          {{ userCurrency || 'THB' }}
        </span>

        <!-- Burger -->
        <button
          type="button"
          class="flex items-center justify-center text-2xl text-white cursor-pointer"
          @click="toggleSidebar"
        >
          <i class="fas fa-bars text-2xl" />
        </button>
      </div>
    </div>

    <div class="bg-secondary text-primary-font lg:hidden">
      <div class="w-full h-7 overflow-hidden flex items-center justify-center">
        <p class="m-0 whitespace-nowrap text-xs font-bold animate-nav-marquee">
          {{ AnnounceList }}
        </p>
      </div>
    </div>

    <!-- ========== DESKTOP HEADER ========== -->
    <div class="hidden lg:flex w-full">
      <!-- LEFT: Logo column -->
      <div class="flex flex-col items-center justify-center px-4 py-2 bg-primary w-65">
        <img :src="agent?.image?.agent?.logo || logo" alt="logo" class="h-11.25 w-47 object-contain" />

        <span class="mt-1 text-[0.7rem] font-semibold text-white text-center">
          {{ safeT('auth.poweredByAskmebet') }}
        </span>
      </div>

      <!-- RIGHT: 2-row panel (user row + marquee) -->
      <div class="flex-1 flex flex-col">
        <!-- TOP ROW: user / role / balance + lang / doc / logout -->
        <div class="flex items-center justify-between px-6 py-3 bg-secondary-background">
          <!-- LEFT: USER / ROLE / BALANCE -->
          <div class="flex items-center space-x-4 text-sm font-bold">
            <span class="flex items-center">
              <i class="far fa-user-circle mr-1" />
              {{ displayUsername }}
            </span>

            <span class="flex items-center">
              <i class="fas fa-briefcase mr-1" />
              {{ displayRole }}
            </span>

            <span class="flex items-center">
              <i class="fas fa-money-bill mr-1" />
              {{ formatCurrency(displayWalletBalance) }}
              {{ userCurrency ? userCurrency : 'THB' }}
            </span>
          </div>

          <!-- RIGHT: LANG / DOC / LOGOUT -->
          <div class="flex items-center gap-2">
            <LanguageButton />
            <DocumentButton />
            <!-- <MobileButton /> -->
            <button
              type="button"
              class="flex items-center text-sm font-semibold hover:opacity-80 transition cursor-pointer hover:text-red-500"
              @click="onLogout()"
            >
              <i class="fas fa-sign-out-alt mr-1" />
              {{ safeT('general.signout') }}
            </button>
          </div>
        </div>

        <!-- BOTTOM ROW: Announcement Bar -->
        <div class="bg-secondary text-sm text-primary-font font-bold py-1">
          <div class="relative w-full h-5 overflow-hidden">
            <p class="absolute w-full m-0 text-center top-1/2 -translate-y-1/2 animate-nav-marquee">
              {{ AnnounceList }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { getModule } from 'vuex-module-decorators';

import AuthModule from '@/store/modules/Auth';
import GeneralModule from '@/store/modules/General';
import CurrencyModule from '@/store/modules/Currency';
import AnnouncementModule from '@/store/modules/Announcement';
import ProfileModule from '@/store/modules/Profile';
import SettingModule from '@/store/modules/Setting';
import WhiteLabelModule from '@/store/modules/WhiteLabel';

import LanguageButton from '@/components/LanguageButton.vue';
// import MobileButton from '@/components/MobileButton.vue';
import DocumentButton from '@/components/DocDownload.vue';

import ErrorHandleMixin from '@/mixins/ErrorHandle.vue';
import jwt_decode from 'jwt-decode';
import Logo from '@/assets/images/logo.png';
import store from '@/store';
import utils from '@/services/utils';

export default defineComponent({
  // eslint-disable-next-line vue/no-reserved-component-names
  name: 'Nav',
  components: {
    LanguageButton,
    // MobileButton,
    DocumentButton
  },
  mixins: [ErrorHandleMixin as any],
  props: {
    toggleSidebar: {
      type: Function,
      required: true
    }
  },
  data() {
    return {
      // Vuex store instances
      AuthInstance: getModule(AuthModule, store),
      GeneralInstance: getModule(GeneralModule, store),
      AnnouncementInstance: getModule(AnnouncementModule, store),
      CurrencyInstance: getModule(CurrencyModule, store),
      ProfileInstance: getModule(ProfileModule, store),
      SettingInstance: getModule(SettingModule, store),
      WhiteLabelInstance: getModule(WhiteLabelModule, store),

      // Announcement text (joined string)
      AnnounceList: '' as string,

      getToken: null as any,
      expJwt: null as any,
      dateNow: null as any,

      userCurrency: 'THB' as string,
      balance: '' as string,

      logo: Logo,
      agent: null as any,

      tokenTimer: null as ReturnType<typeof setTimeout> | null
    };
  },
  computed: {
    user(): any {
      return this.AuthInstance?.user || {};
    },

    displayUsername(): string {
      return String(this.user?.username || '').toUpperCase();
    },

    displayRole(): string {
      return String(this.user?.role || '');
    },

    displayWalletBalance(): number {
      const wallet = this.ProfileInstance?.totalBalance?.wallet;

      if (wallet == null) return 0;

      if (typeof wallet === 'object' && '$numberDecimal' in wallet) {
        const raw = Number((wallet as any).$numberDecimal);
        return Number.isNaN(raw) ? 0 : raw;
      }

      const raw = Number(wallet);
      return Number.isNaN(raw) ? 0 : raw;
    }
  },
  async created() {
    // Start token expiry checker
    this.handleToken();

    // Decode refresh token from Vuex (same as old logic)
    try {
      const token = this.AuthInstance?.tokenRefresh;
      if (token) {
        this.getToken = jwt_decode(token as any);
        this.userCurrency = this.getToken?.currency || 'THB';
      } else {
        this.userCurrency = 'THB';
      }
    } catch (e) {
      console.error('jwt_decode failed in created:', e);
      this.userCurrency = 'THB';
    }

    // Fetch profile balance and settings (alerts handled by axios interceptors)
    try {
      await this.ProfileInstance.GetBalance();
    } catch (err: any) {
      (this as any).checkError?.(err);
    }

    try {
      await this.SettingInstance.GetData();
    } catch (err: any) {
      (this as any).checkError?.(err);
    }

    // Fetch white-label branding data (colors, favicon, etc.)
    let color: any = null;

    try {
      color = await this.WhiteLabelInstance.FetchSettingImage();
    } catch (err: any) {
      (this as any).checkError?.(err);
    }

    const defaultColor = {
      primaryBackground: '#f2f3f4',
      primaryColor: '#0d8c67',
      primaryFont: '#333',
      secondaryBackground: '#ffffff',
      secondaryColor: '#e7dc4e',
      secondaryFont: '#fff',
      thirdFont: '#5c676d'
    };

    this.agent = color;

    // Update favicon
    const faviconEl = document.getElementById('favicon') as HTMLLinkElement | null;
    const href = color?.image?.agent?.favicon;
    if (faviconEl && href) {
      faviconEl.setAttribute('href', href);
    }

    // Apply theme color variables to document body
    const themeColor = color?.agentTheme || defaultColor;
    Object.keys(themeColor).forEach((item: string) => {
      document.body.style.setProperty(`--${item}`, (themeColor as any)[item]);
    });

    // Fetch announcement list and build marquee text
    try {
      await this.AnnouncementInstance.FetchAnnouncement({ type: 'agent' });

      this.AnnounceList = (this.AnnouncementInstance.announcementList || [])
        .filter((item: Record<string, unknown>) => Boolean((item as any).isActive))
        .map((item: Record<string, unknown>) => String((item as any).content ?? ''))
        .filter((s: string) => s.length > 0)
        .join(' | ');
    } catch (err: any) {
      (this as any).checkError?.(err);
    }
  },
  beforeUnmount() {
    if (this.tokenTimer) {
      clearTimeout(this.tokenTimer);
      this.tokenTimer = null;
    }
  },
  methods: {
    safeT(key: unknown) {
      if (typeof key !== 'string' || !key.trim()) {
        return '';
      }

      try {
        return this.$t(key);
      } catch (error) {
        console.warn('i18n key error:', key, error);
        return key;
      }
    },

    // Check refresh token expiry and auto-logout when expired
    handleToken() {
      const storedToken = utils._session.get('ambgames-user-refresh-token');

      if (!storedToken) return;

      try {
        const refreshToken: any = jwt_decode(storedToken);
        const expiredToken = (refreshToken?.exp || 0) * 1000;
        this.dateNow = Date.now();

        if (!expiredToken || this.dateNow >= expiredToken) {
          this.onLogout();
          return;
        }

        this.tokenTimer = setTimeout(() => {
          this.handleToken();
        }, 60000);
      } catch (e) {
        console.error('jwt_decode failed in handleToken:', e);
        this.onLogout();
      }
    },

    formatCurrency(value: any) {
      if (value == null) return '0.00';

      const raw =
        value && typeof value === 'object' && '$numberDecimal' in value
          ? Number((value as any).$numberDecimal)
          : Number(value);

      if (Number.isNaN(raw)) return '0.00';

      return raw.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    // Logout and redirect to login page
    async onLogout() {
      if (this.tokenTimer) {
        clearTimeout(this.tokenTimer);
        this.tokenTimer = null;
      }

      await this.AuthInstance.Logout();

      if ((this as any).$route.path !== '/login') {
        await (this as any).$router.push('/login');
      }
    }
  }
});
</script>

<style>
@keyframes nav-marquee {
  0% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(-100%);
  }
}

@layer utilities {
  .animate-nav-marquee {
    animation: nav-marquee 20s linear infinite;
    will-change: transform;
  }
}
</style>
